import { CustomerCompany } from 'module/api/company/types';
import { CenterAlignedBox } from 'module/ui/components/atoms/layout/layout';
import { Loading } from 'module/ui/components/atoms/loading';
import { Popover } from 'module/ui/components/atoms/popover';
import {
  HeadingCompact01,
  Short01,
  ShortSB,
} from 'module/ui/components/styles';
import { ICellRendererParams } from 'ag-grid-community';
import { SignalType } from 'api/signal/types';
import { keyBy } from 'lodash';
import { useGetAllSignalConfigurationsQuery } from 'queries/signal';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { sortByText } from 'utils/sort-by-text';
import { signalTypeConfigs } from 'components/organisms/signal/signal-type/type-configs';

const IndicatorWrapper = styled(CenterAlignedBox)`
  display: flex;
  gap: var(--spacing-01);
  cursor: pointer;
  height: 100%;
`;
const Indicator = styled.div<{ signalType?: SignalType }>`
  height: 8px;
  width: 8px;
  background-color: ${({ signalType }) =>
    signalType
      ? signalTypeConfigs[signalType].colorVariable
      : 'var(--scarlet-gray-50)'};
  transition: background-color 0.2s ease;
`;
const Row = styled.div`
  display: flex;
  gap: var(--spacing-02);
  justify-content: space-between;
  align-items: center;
`;
const Header = styled.div`
  ${HeadingCompact01};
`;
const TotalCount = styled.div`
  ${Short01};
  color: var(--text-secondary);
`;
const SignalName = styled.div`
  ${Short01};
`;
const SignalCount = styled.div`
  ${ShortSB};
  color: var(--text-secondary);
`;
const Divider = styled.div`
  height: 1px;
  width: 100%;
  background-color: var(--field-border-default);
`;
const ContentWrapper = styled.div`
  padding: var(--spacing-05);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
  min-width: 310px;
`;

const Content = ({
  occurrences,
  numberOfWeeks,
  signalType,
}: {
  occurrences: Array<{
    signalName: string;
    signalConfigurationId: number;
    signalOccurrenceCount: number;
    signalOccurrenceIds: number[];
    lastOccurredAt: string;
  }>;
  numberOfWeeks: number;
  signalType: SignalType;
}) => {
  const { data: signalConfigurations, isLoading: isConfigurationsLoading } =
    useGetAllSignalConfigurationsQuery();
  const configurationsToShow = useMemo(() => {
    const configsForType = (signalConfigurations ?? []).filter(
      (config) => config.signalType === signalType,
    );
    const occurrenceCounts = keyBy(occurrences, 'signalConfigurationId');
    const existingConfigs = configsForType.map((config) => ({
      id: config.signalConfigurationId,
      signalName: config.signalName,
      signalOccurrenceCount:
        occurrenceCounts[config.signalConfigurationId]?.signalOccurrenceCount,
    }));
    const missingConfigs = occurrences
      .filter(
        (occurrence) =>
          !configsForType.find(
            (config) =>
              config.signalConfigurationId === occurrence.signalConfigurationId,
          ),
      )
      .map((occurrence) => ({
        id: occurrence.signalConfigurationId,
        signalName: occurrence.signalName,
        signalOccurrenceCount: occurrence.signalOccurrenceCount,
      }));

    return [...existingConfigs, ...missingConfigs].sort((a, b) =>
      sortByText(a.signalName, b.signalName),
    );
  }, [occurrences, signalConfigurations, signalType]);
  const totalCount = useMemo(
    () =>
      occurrences.reduce((acc, curr) => acc + curr.signalOccurrenceCount, 0),
    [occurrences],
  );

  return (
    <ContentWrapper>
      <Row>
        <Header>
          <FormattedMessage
            id="account.people.column.recent_signals.popover.title"
            values={{ count: numberOfWeeks }}
          />
        </Header>
        <TotalCount>
          <FormattedMessage
            id={
              signalType === SignalType.Risk
                ? 'account.people.column.recent_signals.popover.total_risks'
                : 'account.people.column.recent_signals.popover.total_opportunities'
            }
            values={{ count: totalCount }}
          />
        </TotalCount>
      </Row>
      <Divider />
      {isConfigurationsLoading ? (
        <Loading />
      ) : (
        <>
          {configurationsToShow.map((configuration) => (
            <Row key={configuration.id}>
              <SignalName>{configuration.signalName}</SignalName>
              <SignalCount>
                {configuration.signalOccurrenceCount ?? '-'}
              </SignalCount>
            </Row>
          ))}
        </>
      )}
    </ContentWrapper>
  );
};

export const RecentRisksCell = (
  params: ICellRendererParams<CustomerCompany>,
) => {
  const { data, value } = params;

  // Use value if it's the full data object, otherwise use data
  const rowData =
    value && typeof value === 'object' && value.recentRiskSignalWeeklyCounts
      ? value
      : data;

  if (!rowData || !rowData.recentRiskSignalWeeklyCounts) {
    return '-';
  }

  return (
    <Popover
      content={
        <Content
          occurrences={rowData.recentRiskSignals ?? []}
          numberOfWeeks={rowData.recentRiskSignalWeeklyCounts.length}
          signalType={SignalType.Risk}
        />
      }
    >
      <IndicatorWrapper>
        {rowData.recentRiskSignalWeeklyCounts.map((count, index) => (
          <Indicator
            key={index}
            signalType={count > 0 ? SignalType.Risk : undefined}
          />
        ))}
      </IndicatorWrapper>
    </Popover>
  );
};

export const RecentOpportunitiesCell = (
  params: ICellRendererParams<CustomerCompany>,
) => {
  const { data, value } = params;

  // Use value if it's the full data object, otherwise use data
  const rowData =
    value &&
    typeof value === 'object' &&
    value.recentOpportunitySignalWeeklyCounts
      ? value
      : data;

  if (!rowData || !rowData.recentOpportunitySignalWeeklyCounts) {
    return '-';
  }

  return (
    <Popover
      content={
        <Content
          occurrences={rowData.recentOpportunitySignals ?? []}
          numberOfWeeks={rowData.recentOpportunitySignalWeeklyCounts.length}
          signalType={SignalType.Opportunity}
        />
      }
    >
      <IndicatorWrapper>
        {rowData.recentOpportunitySignalWeeklyCounts.map((count, index) => (
          <Indicator
            key={index}
            signalType={count > 0 ? SignalType.Opportunity : undefined}
          />
        ))}
      </IndicatorWrapper>
    </Popover>
  );
};

export const RecentOperationalSignalsCell = (
  params: ICellRendererParams<CustomerCompany>,
) => {
  const { data, value } = params;

  // Use value if it's the full data object, otherwise use data
  const rowData =
    value &&
    typeof value === 'object' &&
    value.recentOperationalSignalWeeklyCounts
      ? value
      : data;

  if (!rowData || !rowData.recentOperationalSignalWeeklyCounts) {
    // If we have an object but it's not the right structure, show a simple indicator
    if (value && typeof value === 'object') {
      return <div style={{ color: 'var(--text-secondary)' }}>No data</div>;
    }

    return '-';
  }

  return (
    <Popover
      content={
        <Content
          occurrences={rowData.recentOperationalSignals ?? []}
          numberOfWeeks={rowData.recentOperationalSignalWeeklyCounts.length}
          signalType={SignalType.Operational}
        />
      }
    >
      <IndicatorWrapper>
        {rowData.recentOperationalSignalWeeklyCounts.map((count, index) => (
          <Indicator
            key={index}
            signalType={count > 0 ? SignalType.Operational : undefined}
          />
        ))}
      </IndicatorWrapper>
    </Popover>
  );
};
